import {DataTable} from "../DataTable/DataTable";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {ServiceTypeFacetModeEntity} from "@ots-frontend/ots-data";
import {useServiceTypeFacetModeDataTableFilter} from "./useServiceTypeFacetModeDataTableFilter";
import {useServiceTypeFacetModeMutation} from "../../../hooks/useServiceTypeFacetModeMutation";
import {Badge} from "@ots-frontend/ots-ui";

const EMPTY_ARRAY: ServiceTypeFacetModeEntity[] = []

export type ServiceEventTypeDataTableProps = EntityDataTableProps<ServiceTypeFacetModeEntity>

export const ServiceTypeFacetModeDataTable = (props: ServiceEventTypeDataTableProps) => {
  const {
    records: facets = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useServiceTypeFacetModeDataTableFilter({
    records: facets
  })

  const sortConfig = useMemo(() => {
    return {
      "facet": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.facet?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'serviceEventType', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    deleteServiceTypeFacetMode,
  } = useServiceTypeFacetModeMutation()

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: ServiceTypeFacetModeEntity) => {
    e.stopPropagation()
    deleteServiceTypeFacetMode(record.id, undefined)
  }, [deleteServiceTypeFacetMode])

  const columns = useMemo(() => {
    const values: DataTableColumn<ServiceTypeFacetModeEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "facet"),
        title: t("common.entity.servicetypefacetmode.table.column.facet", "Facet"),
        accessor: 'facet',
        width: 150,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "mode"),
        title: t("common.entity.servicetypefacetmode.table.column.mode", "Mode"),
        accessor: 'mode',
        width: 150,
        sortable: true,
      },
      {
        title: t("common.entity.servicetypefacetmode.table.column.primary", "Primary"),
        accessor: 'primary',
        width: 100,
        sortable: true,
        render: ( ({primary}) => {
          return primary ? <Badge>{t("common.shared.status.primary", "Primary")}</Badge> : undefined
        })
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: (company) => (
            <EntityActionGroup
                permissionPrefix={"servicetype"}
                view={false}
                edit={false}
                delete={true}
                onDeleteActionClick={(e) => onDeleteActionClick(e, company)}
            />
        )
      }
    ]

    return values
  }, [filterConfig, onDeleteActionClick, t])

  return (
      <DataTable
          {...rest}
          {...(pagination ?? {})}
          sortStatus={sortStatus}
          onSortStatusChange={onSortStatusChange}
          columns={columns}
      />
  )
}
